import React, { useEffect, useState ,useRef} from "react";
import { Box, Drawer, IconButton, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import save from "../../../../assets/save.svg";
import WorkersMultiSelectDropdown from "../../../../components/workersMultiSelection";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useSelector } from "react-redux";
import { ATTENDANCE_HISTORY_STATUS_FILTERS } from "./attendanceHistoryStatusOptions";
import { getStatusBadgeBackgroundColor } from "../../../../constants/statusPalette";
import { getAttendanceHistoryColorKey } from "./attendanceHistoryStatusOptions";

export default function AttendanceFilterDrawer({
  open,
  onClose,
  filters,
  setFilters,
  setIsFilterApplied,
}) {
  const { t } = useTranslation();

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;

  const [workers, setWorkers] = useState([]);
  const [localFilters, setLocalFilters] = useState(filters);
  const startRef = useRef(null);
  const endRef = useRef(null);
  // =======================
  // FETCH WORKERS
  // =======================
  const fetchWorkers = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/public/workers?no_pagination=true&company_id=${companyId}&status=active`,
        {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        }
      );

      const data = await res.json();
      const list = data?.data?.records || [];

      setWorkers([
        { label: t("all"), value: "all" },
        ...list.map((w) => ({
          label: `${w.first_name} ${w.last_name}`,
          value: w.id,
          email: w.email,
        })),
      ]);
    } catch (err) {
      console.error("Worker fetch error", err);
    }
  };

  // =======================
  // SYNC ON OPEN
  // =======================
  useEffect(() => {
    if (open) {
      setLocalFilters(filters);
      fetchWorkers();
    }
  }, [open]);

  // =======================
  // APPLY FILTER
  // =======================
  const apply = () => {
    setFilters(localFilters);
    setIsFilterApplied(
      Boolean(
        localFilters.status ||
        localFilters.startDate ||
        localFilters.endDate ||
        localFilters.workerId
      )
    );
    onClose();
  };

  // =======================
  // CLEAR FILTER
  // =======================
  const clear = () => {
    const cleared = {
      status: "",
      startDate: "",
      endDate: "",
      workerId: "",
    };
    setLocalFilters(cleared);
    setFilters(cleared);
    setIsFilterApplied(false);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400, md: 550 },
          backgroundColor: "#E7EBEE",
        },
      }}
    >
      {/* ================= HEADER ================= */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#E7EBEE",
          zIndex: 10,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #ccc",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            
            }}
          >
            {t("attendanceFilter.title")}
          </CustomText>
        </Box>
      </Box>

      {/* ================= BODY ================= */}
      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            px: 3,
            py: 3,
            height: "calc(100vh - 90px)",
            overflowY: "auto",
          }}
        >
          {/* ===== STATUS ===== */}
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 500,
              color: "#2C384C",
                textAlign:"left"
            }}
          >
            {t("attendanceFilter.statusHeading")}
          </CustomText>
         <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
           {ATTENDANCE_HISTORY_STATUS_FILTERS.map((opt) => {
              const isSelected = localFilters.status === opt.value;
              const statusAccent = getStatusBadgeBackgroundColor(
                getAttendanceHistoryColorKey(opt.value)
              );
              return (
                <Button
                  key={opt.key}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newStatus = isSelected ? "" : opt.value;
                    setLocalFilters((prev) => ({ ...prev, status: newStatus }));
                  }}
                       sx={{
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    textTransform: "none",
                    py: 0.8,
                    px: 2,
                    color: isSelected ? statusAccent : "#363333",
                    border: isSelected
                      ? `2px solid ${statusAccent}`
                      : "2px solid #091E4224",
                    backgroundColor: "transparent",
                  }}
                >
                  {t(opt.labelKey)}
                </Button>
              );
            })}
          </Box>

          {/* ===== WORKER ===== */}
          <Box mt={3}>
            <WorkersMultiSelectDropdown
              label={t("employee")}
              values={
                localFilters.workerId ? [localFilters.workerId] : []
              }
              options={workers}
              enableSearch
              isSingleSelect
              onChange={(e) => {
                const value = e.target.value?.[0] || "";
                setLocalFilters((p) => ({
                  ...p,
                  workerId: value === "all" ? "" : value,
                }));
              }}
              renderValue={(selected) =>
                workers.find((w) => w.value === selected[0])?.label || ""
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <span>{option.label}</span>
                  {option.email && (
                    <span style={{ fontSize: 12, color: "#888" }}>
                      {option.email}
                    </span>
                  )}
                </li>
              )}
            />
          </Box>

          {/* ===== DATE FROM ===== */}
          <Box mt={3}>
            <label className="field_label">
              {t("filterDrawer.startDate")}
            </label>
            <input
             ref={startRef}
                onClick={() => startRef.current?.showPicker()} 
              type="date"
              value={localFilters.startDate || ""}
              onChange={(e) =>
                setLocalFilters((p) => ({
                  ...p,
                  startDate: e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40 }}
            />
          </Box>

          {/* ===== DATE TO ===== */}
          <Box mt={3}>
            <label className="field_label">
              {t("filterDrawer.endDate")}
            </label>
            <input
             ref={endRef}
                onClick={() => endRef.current?.showPicker()} 
              type="date"
              value={localFilters.endDate || ""}
              onChange={(e) =>
                setLocalFilters((p) => ({
                  ...p,
                  endDate: e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40 }}
            />
          </Box>

          {/* ===== BUTTONS ===== */}
          <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
            <CustomButton
              onClick={apply}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img src={save} alt="save" width={16} height={16} />
                  {t("attendanceFilter.apply")}
                </Box>
              }
            />
            <Button
              variant="outlined"
              onClick={clear}
              sx={{
                fontSize: 15,
                textTransform: "none",
                borderRadius: "5px",
                color: "#006EC2",
                border: "2px solid #006EC2",
                px: 3,
              }}
            >
              {t("attendanceFilter.clear")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}





